import Extent from "@arcgis/core/geometry/Extent";
import Point from "@arcgis/core/geometry/Point";
import Polygon from "@arcgis/core/geometry/Polygon";
import Graphic from "@arcgis/core/Graphic";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import { PREFERRED_WKID } from "../../../CustomAreas/UserDataContextContainer";
import { geodesicBuffer } from "@arcgis/core/geometry/geometryEngine";
import { Properties } from "@turf/helpers";
import { IClashToForget } from "../../ObservationsContextContainer";

export function findMinAndMaxPoint(polygons: Polygon[]) {
    let minLongitude: number = 1000000000;
    let maxLongitude: number = -1000000000;
    let minLatitude: number = 1000000000;
    let maxLatitude: number = -1000000000;

    for (let index = 0; index < polygons.length; index++) {
        const element = polygons[index];

        const point = element.rings[0][0];

        const longitude = point[0];
        const latitude = point[1];

        if (longitude <= minLongitude && latitude <= minLatitude) {
            minLongitude = longitude;
            minLatitude = latitude;
        }

        if (longitude >= maxLongitude && latitude >= maxLatitude) {
            maxLongitude = longitude;
            maxLatitude = latitude;
        }
    }

    const minPoint = new Point({
        longitude: minLongitude,
        latitude: minLatitude,
        spatialReference: { wkid: PREFERRED_WKID }
    });

    const maxPoint = new Point({
        longitude: maxLongitude,
        latitude: maxLatitude,
        spatialReference: { wkid: PREFERRED_WKID }
    });

    return [minPoint, maxPoint];
};

export function getLajiFiObservationsAsGraphics(jsonResponse: any): Graphic[] {
    return jsonResponse.features.map((feature: any) => {
        return new Graphic({
            symbol: new SimpleMarkerSymbol({
                color: "red",
                size: 10
            }),
            geometry: new Point({
                longitude: feature.geometry.coordinates[0],
                latitude: feature.geometry.coordinates[1]
            }),
            attributes: feature.properties
        });
    });
};

export function createRadiusGraphicForPoint(point: Point, radius: number, featureProperties: Properties) {
    const bufferedPoint = geodesicBuffer(point, radius, "meters") as Polygon;

    const radiusPoly: Polygon = new Polygon({
        rings: bufferedPoint.rings,
        spatialReference: { wkid: PREFERRED_WKID }
    });

    const radiusGraphic = new Graphic({
        attributes: { ...featureProperties, isRadius: 1 },
        geometry: radiusPoly
    });

    return radiusGraphic;
}

export function getClashToForgetFromFeature(feature: Graphic): IClashToForget {
    return {
        id: feature.attributes["id"],
        unitId: feature.attributes["unit.unitId"],
        forgetDate: new Date().toISOString(),
        declarationArrivalDate: feature.attributes["declarationArrivalDate"]
    };
}

export async function getLajiFiObservationsFetch(geometryInPreferredWkid?: Extent, additionalQueryString?: object, centerPoint: boolean = true, signal?: AbortSignal) {
    let url = new URL("https://api.laji.fi/v0/warehouse/query/unit/list?");
    const params = new URLSearchParams({
        "secureLevel": "NONE",
        "access_token": `${process.env.REACT_APP_LAJIFI_TOKEN}`,
        "featureType": centerPoint ? "CENTER_POINT" : "ORIGINAL_FEATURE",
        "countryId": "ML.206",
        "redListStatusId": "MX.iucnCR,MX.iucnEN,MX.iucnVU",
        "cache": "true",
        "coordinateAccuracyMax": "100",
        "qualityIssues": "NO_ISSUES",
        "selected": "unit.unitId,unit.linkings.taxon.id,document.collectionId,gathering.eventDate.begin,gathering.eventDate.end,gathering.interpretations.coordinateAccuracy",
        "format": "geojson",
        ...additionalQueryString,
        "pageSize": "10000",
        "coordinates": geometryInPreferredWkid ? `${geometryInPreferredWkid.ymin}:${geometryInPreferredWkid.ymax}:${geometryInPreferredWkid.xmin}:${geometryInPreferredWkid.xmax}:WGS84` : ""
    });

    url.search = params.toString();

    return await fetch(url.toString(), {
        signal
    });
};

export function getSquareSizeBasedOnZoom(currentZoom: number): number {
    switch (currentZoom) {
        case 15:
            return 0.1;
        case 14:
            return 0.2;
        case 13:
            return 0.3;
        case 12:
            return 0.5;
        case 11:
            return 0.4;
        case 10:
            return 0.7;
        default:
            return 1;
    }
};

export enum MovingAxis {
    latitude,
    longitude
};